import { Add, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid2,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { getManagedProjects } from "../../api/app";
import { createProcedure } from "../../api/procedure";
import { getProcedureTypes } from "../../api/procedure/getProcedureTypes";
import { getProjectActivities } from "../../api/project";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { Activity } from "../../types/model/activity";
import { CreateProcedureRequest } from "../../types/model/procedure";
import { Project } from "../../types/model/project";
import { StoreInterfaces } from "../../types/store";
import ActivityQuickCreateDialog from "../Activity/ActivityQuickCreateDialog";
import ProjectQuickCreateDialog from "../Projects/ProjectQuickCreateDialog";

interface ProcedureWizardDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ProcedureFormValues {
  name: string;
  description: string;
  procedureTypeId: string;
  startDate: string;
  endDate: string;
}

const ProcedureWizardDialog: FC<ProcedureWizardDialogProps> = ({
  open,
  onClose,
  onSuccess,
}): ReactElement => {
  const { t } = useTranslationHook();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formStyles = useFormStyles();
  const session = useSelector((state: StoreInterfaces) => state.session);

  const steps = [
    t("procedures.wizard.select-project"),
    t("procedures.wizard.select-activity"),
    t("procedures.wizard.details"),
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitySearch, setActivitySearch] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);

  const [projectCreateOpen, setProjectCreateOpen] = useState(false);
  const [activityCreateOpen, setActivityCreateOpen] = useState(false);

  const [procedureSubmitting, setProcedureSubmitting] = useState(false);
  const [procedureTypes, setProcedureTypes] = useState<{ id: number; type: string; i18nLabel: string }[]>([]);

  useEffect(() => {
    if (open && activeStep === 0) {
      fetchProjects();
    }
  }, [open, activeStep]);

  useEffect(() => {
    if (selectedProject && activeStep === 1) {
      fetchActivities(selectedProject.id);
    }
  }, [selectedProject, activeStep]);

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const data = await getManagedProjects();
      setProjects(data);
      return data;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchActivities = async (projectId: number) => {
    setActivitiesLoading(true);
    try {
      const data = await getProjectActivities(projectId);
      setActivities(data);
      return data;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    const loadProcedureTypes = async () => {
      try {
        dispatch(setLoading(true));
        const result = await getProcedureTypes();
        setProcedureTypes(result);
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(setLoading(false));
      }
    };

    if (procedureTypes.length === 0) loadProcedureTypes();
  }, []);

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setSelectedActivity(null);
    setActivities([]);
    setActiveStep(1);
  };

  const handleProjectCreate = async (projectId: number) => {
    const data = await fetchProjects();
    const created = data.find((p) => p.id === projectId);
    if (created) handleProjectSelect(created);
    setProjectCreateOpen(false);
  };

  const handleActivitySelect = (activity: Activity) => {
    setSelectedActivity(activity);
    setActiveStep(2);
  };

  const handleActivityCreate = async (activityId: number) => {
    if (!selectedProject) return;

    const data = await fetchActivities(selectedProject.id);
    const created = data.find((a) => a.id === activityId);
    if (created) handleActivitySelect(created);
    setActivityCreateOpen(false);
  };

  const handleProcedureSubmit = async (values: ProcedureFormValues) => {
    if (!selectedProject || !selectedActivity) {
      enqueueSnackbar(t("generic.error"), { variant: "error" });
      return;
    }

    setProcedureSubmitting(true);
    dispatch(setLoading(true));

    try {
      const request: CreateProcedureRequest = {
        name: values.name,
        description: values.description,
        procedureTypeId: Number.parseInt(values.procedureTypeId),
        ownerId: session?.userId || 0,
        accountId: session?.accountId || 0,
        managerId: session?.userId || 0,
        ...(values.startDate ? { startDate: moment(values.startDate).toDate() } : {}),
        ...(values.endDate ? { endDate: moment(values.endDate).toDate() } : {}),
      };

      const response = await createProcedure(
        request,
        selectedProject.id,
        selectedActivity.id
      );

      const procedureId = response?.procedure?.id;
      if (!procedureId) return;

      enqueueSnackbar(t("procedures.created"), { variant: "success" });

      const accountId = session?.accountId;
      if (!accountId) return;

      navigate(
        `/accounts/${accountId}/projects/${selectedProject.id}/activities/${selectedActivity.id}/procedures/${procedureId}`
      );

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setProcedureSubmitting(false);
      dispatch(setLoading(false));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedProject(null);
    setSelectedActivity(null);
    setProjects([]);
    setActivities([]);
    onClose();
  };

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const filteredActivities = activities.filter((a) =>
    a.name.toLowerCase().includes(activitySearch.toLowerCase())
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          onClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("procedures.create")} - {steps[activeStep]}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 3,
              "& .MuiStepIcon-root.Mui-completed": {
                color: "secondary.main",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* STEP 0 - Project */}
          {activeStep === 0 && (
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder={t("generic.search")}
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mb: 2 }}
              />

              {projectsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: "auto", mb: 2 }}>
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <Card
                        key={project.id}
                        sx={{
                          m: 1.5,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": { boxShadow: 2 },
                        }}
                      >
                        <CardActionArea onClick={() => handleProjectSelect(project)}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {project.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                              {project.description}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ))
                  ) : (
                    <Typography align="center">{t("generic.no-data")}</Typography>
                  )}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 1,
                  px: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t("projects.not-found-description")}
                </Typography>

                <Button
                  size="small"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={() => setProjectCreateOpen(true)}
                >
                  {t("projects.create")}
                </Button>
              </Box>
            </Box>
          )}

          {/* STEP 1 - Activity */}
          {activeStep === 1 && selectedProject && (
            <Box>
              <Grid2 container spacing={1} sx={{ mb: 2 }}>
                <Grid2 size={6}>
                  <Typography variant="caption" color="textSecondary">
                    {t("generic.project")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedProject.name}
                  </Typography>
                </Grid2>
              </Grid2>

              <TextField
                fullWidth
                size="small"
                placeholder={t("generic.search")}
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mb: 2 }}
              />

              {activitiesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: "auto", mb: 2 }}>
                  {filteredActivities.length > 0 ? (
                    filteredActivities.map((activity) => (
                      <Card
                        key={activity.id}
                        sx={{
                          m: 1.5,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": { boxShadow: 2 },
                        }}
                      >
                        <CardActionArea onClick={() => handleActivitySelect(activity)}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {activity.name}
                            </Typography>
                            {activity.description && (
                              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                {activity.description}
                              </Typography>
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ))
                  ) : (
                    <Typography align="center">{t("generic.no-data")}</Typography>
                  )}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 1,
                  px: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t("activities.not-found-description")}
                </Typography>

                <Button
                  size="small"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={() => setActivityCreateOpen(true)}
                >
                  {t("activities.create")}
                </Button>
              </Box>
            </Box>
          )}

          {/* STEP 2 - Details */}
          {activeStep === 2 && selectedProject && selectedActivity && (
            <Formik<ProcedureFormValues>
              initialValues={{
                name: "",
                description: "",
                procedureTypeId: "",
                startDate: "",
                endDate: "",
              }}
              validationSchema={Yup.object().shape({
                name: Yup.string().required(t("procedures.name-required")),
                description: Yup.string().required(t("procedures.description-required")),
                procedureTypeId: Yup.string().required(t("procedures.type-required")),
                startDate: Yup.string().nullable(),
                endDate: Yup.string().nullable(),
              })}
              onSubmit={handleProcedureSubmit}
            >
              {({ values, errors, touched, handleSubmit, handleBlur, setFieldValue }) => (
                <Box component="form" id="procedure-form" onSubmit={handleSubmit}>
                  <Grid2 container spacing={1} sx={{ mb: 3 }}>
                    <Grid2 size={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t("generic.project")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedProject.name}
                      </Typography>
                    </Grid2>
                    <Grid2 size={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t("generic.activity")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedActivity.name}
                      </Typography>
                    </Grid2>
                  </Grid2>

                  <Grid2 container spacing={2}>
                    <Grid2 size={12}>
                      <TextField
                        fullWidth
                        required
                        name="name"
                        label={t("generic.name")}
                        value={values.name}
                        onChange={(e) => setFieldValue("name", e.target.value)}
                        onBlur={handleBlur}
                        error={!!(errors.name && touched.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid2>

                    <Grid2 size={12}>
                      <FormControl
                        fullWidth
                        required
                        error={!!(errors.procedureTypeId && touched.procedureTypeId)}
                      >
                        <InputLabel id="procedure-type-label">
                          {t("procedures.procedure-type")}
                        </InputLabel>
                        <Select
                          labelId="procedure-type-label"
                          name="procedureTypeId"
                          value={values.procedureTypeId}
                          label={t("procedures.procedure-type")}
                          onChange={(e) => setFieldValue("procedureTypeId", e.target.value)}
                          onBlur={handleBlur}
                        >
                          {procedureTypes.map((type) => (
                            <MenuItem key={type.id} value={String(type.id)}>
                              {t(type.i18nLabel)}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {touched.procedureTypeId && errors.procedureTypeId}
                        </FormHelperText>
                      </FormControl>
                    </Grid2>

                    <Grid2 size={12}>
                      <TextField
                        fullWidth
                        required
                        name="description"
                        label={t("generic.description")}
                        multiline
                        rows={3}
                        value={values.description}
                        onChange={(e) => setFieldValue("description", e.target.value)}
                        onBlur={handleBlur}
                        error={!!(errors.description && touched.description)}
                        helperText={touched.description && errors.description}
                      />
                    </Grid2>

                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        sx={{ fontWeight: 600, mb: 1, mt: 1.5 }}
                      >
                        {t("projects.dates")}
                      </Typography>

                      <Grid2 container spacing={2}>
                        <Grid2 size={6}>
                          <DatePicker
                            label={t("procedures.start-date")}
                            value={values.startDate ? moment(values.startDate) : null}
                            onChange={(date) =>
                              setFieldValue("startDate", date ? date.toISOString() : "")
                            }
                            slotProps={{
                              field: { clearable: true },
                              textField: {
                                fullWidth: true,
                                error: !!(errors.startDate && touched.startDate),
                                helperText: touched.startDate && errors.startDate ? errors.startDate : "",
                                sx: { ...formStyles },
                              },
                            }}
                            format="DD/MM/YYYY"
                            views={["day", "month", "year"]}
                          />
                        </Grid2>

                        <Grid2 size={6}>
                          <DatePicker
                            label={t("procedures.end-date")}
                            value={values.endDate ? moment(values.endDate) : null}
                            onChange={(date) =>
                              setFieldValue("endDate", date ? date.toISOString() : "")
                            }
                            slotProps={{
                              field: { clearable: true },
                              textField: {
                                fullWidth: true,
                                error: !!(errors.endDate && touched.endDate),
                                helperText: touched.endDate && errors.endDate ? errors.endDate : "",
                                sx: { ...formStyles },
                              },
                            }}
                            format="DD/MM/YYYY"
                            views={["day", "month", "year"]}
                          />
                        </Grid2>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Box>
              )}
            </Formik>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button onClick={handleClose} color="primary">
            {t("generic.cancel")}
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            {activeStep > 0 && (
              <Button onClick={handleBack} variant="outlined" color="secondary">
                {t("generic.back")}
              </Button>
            )}

            {activeStep === steps.length - 1 && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  const form = document.getElementById("procedure-form") as HTMLFormElement | null;
                  form?.requestSubmit();
                }}
                disabled={procedureSubmitting}
              >
                {procedureSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  t("generic.create")
                )}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Project Creation Dialog */}
      <ProjectQuickCreateDialog
        open={projectCreateOpen}
        onClose={() => setProjectCreateOpen(false)}
        onSuccess={handleProjectCreate}
      />

      {/* Activity Creation Dialog */}
      {selectedProject && (
        <ActivityQuickCreateDialog
          open={activityCreateOpen}
          onClose={() => setActivityCreateOpen(false)}
          projectId={selectedProject.id}
          onSuccess={handleActivityCreate}
        />
      )}
    </>
  );
};

export default ProcedureWizardDialog;